import type {
  AbleitungsmodifikationenRepraesentation,
  AbleitungsmodifikationenStruktur,
  Anzahl,
  SchemaElementArt,
} from "../common";
import { InternalParserError } from "../errors";
import type { Table } from "../table";

export interface GenericodeIdentification {
  version: string | undefined;
  canonicalIdentification: string;
  canonicalVersionUri: string;
}

export interface CodelisteReferenz {
  id: string;
  version?: string;
  genericode: GenericodeIdentification;
}

export interface ElementReference {
  type: "dataField" | "dataGroup";
  identifier: string;
  bezug?: string;
  anzahl: Anzahl;
}

export type Feldart = "input" | "select" | "label";

export function parseFeldart(value: string): Feldart {
  switch (value) {
    case "input":
    case "select":
    case "label":
      return value;
    default:
      throw new InternalParserError(`Invalid Feldart: ${value}`);
  }
}

export type Datentyp =
  | "text"
  | "date"
  | "bool"
  | "num"
  | "num_int"
  | "num_currency"
  | "file"
  | "obj";

export function parseDatentyp(value: string): Datentyp {
  switch (value) {
    case "text":
    case "date":
    case "bool":
    case "num":
    case "num_int":
    case "num_currency":
    case "file":
    case "obj":
      return value;
    default:
      throw new InternalParserError(`Invalid Datentyp: ${value}`);
  }
}

export type ElementStatus = "inVorbereitung" | "aktiv" | "inaktiv";

export function parseElementStatus(value: string): ElementStatus {
  switch (value) {
    case "inVorbereitung":
    case "aktiv":
    case "inaktiv":
      return value;
    default:
      throw new InternalParserError(`Invalid Status: ${value}`);
  }
}

export interface BaseData {
  identifier: string;
  id: string;
  version?: string;
  name: string;
  bezeichnungEingabe?: string;
  bezeichnungAusgabe?: string;
  beschreibung?: string;
  definition?: string;
  bezug?: string;
  status: ElementStatus;
  gueltigAb?: Date;
  gueltigBis?: Date;
  fachlicherErsteller?: string;
  versionshinweis?: string;
  freigabedatum?: Date;
  veroeffentlichungsdatum?: Date;
}

export interface Regel extends BaseData {
  script?: string;
}

export interface ElementData extends BaseData {
  schemaelementart: SchemaElementArt;
  hilfetextEingabe?: string;
  hilfetextAusgabe?: string;
}

export interface Datenfeld extends ElementData {
  feldart: Feldart;
  datentyp: Datentyp;
  praezisierung?: string;
  inhalt?: string;
  codelisteReferenz?: CodelisteReferenz;
  regeln: Array<string>;
}

export interface Datenfeldgruppe extends ElementData {
  elemente: Array<ElementReference>;
  regeln: Array<string>;
}

export interface Stammdatenschema extends BaseData {
  hilfetext?: string;
  ableitungsmodifikationenStruktur: AbleitungsmodifikationenStruktur;
  ableitungsmodifikationenRepraesentation: AbleitungsmodifikationenRepraesentation;
  regeln: Array<string>;
  elemente: Array<ElementReference>;
}

export class SchemaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SchemaError";
  }
}

export class SchemaContainer {
  public schema: Stammdatenschema;

  public datenfeldgruppen: Table<Datenfeldgruppe>;
  public datenfelder: Table<Datenfeld>;
  public regeln: Table<Regel>;

  constructor(
    schema: Stammdatenschema,
    datenfeldgruppen: Table<Datenfeldgruppe>,
    datenfelder: Table<Datenfeld>,
    regeln: Table<Regel>,
  ) {
    this.schema = schema;
    this.datenfeldgruppen = datenfeldgruppen;
    this.datenfelder = datenfelder;
    this.regeln = regeln;
  }

  public getCodeLists(): CodelisteReferenz[] {
    const lists: Record<string, CodelisteReferenz> = {};

    for (const datenfeld of Object.values(this.datenfelder.entries())) {
      const { codelisteReferenz } = datenfeld;
      if (codelisteReferenz) {
        lists[codelisteReferenz.genericode.canonicalVersionUri] =
          codelisteReferenz;
      }
    }

    return Object.values(lists);
  }
}
